import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import Clock from 'react-live-clock';
import { useTranslation } from 'react-i18next';
import logo from '../assets/Navbar/logo.png';
import { FaRegEnvelope, FaWhatsapp } from 'react-icons/fa';
import { LuPhone } from 'react-icons/lu';
import { SlLocationPin } from 'react-icons/sl';
import { GiHamburgerMenu } from 'react-icons/gi';

// Define keyframes for slide-in animation
const slideIn = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { t, i18n } = useTranslation('navbar'); // Specify the 'navbar' namespace


    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleLanguageChange = (event) => {
        const selectedLanguage = event.target.value;
        i18n.changeLanguage(selectedLanguage);
    };



    return (
        <div>
            {/* Inject keyframes for animation */}
            <style>{slideIn}</style>

            {/* Small Screen Section (below md, <768px) */}
            <div
                className="block md:hidden border-b-4 border-[#1F5D42] relative"
                style={{
                    backgroundColor: '#FFFFFF',
                }}
            >
                {/* Centered Logo */}
                <div className="flex justify-center pt-4 pb-4">
                    <img
                        src={logo}
                        alt="Mawathage Services"
                        className="h-48 xs:h-32 sm:h-36 md:h-40"
                    />
                </div>

                {/* Clock + Location + Language (top right) */}
                <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
                    <div className="bg-[#f8f8f8] px-3 py-1.5 rounded-md text-[#0D4E4A] text-sm font-medium shadow-sm">
                        <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                    </div>
                    <h3 className="text-xs sm:text-sm text-[#1F5D42] font-medium text-center sm:whitespace-nowrap">
                        <div className="sm:hidden">Moragalla<br />Sri Lanka</div>
                        <div className="hidden sm:block">Moragalla / Sri Lanka</div>
                    </h3>
                    <select
                        onChange={handleLanguageChange}
                        value={i18n.language}
                        className="
                            w-[50px]
                            border border-[#0D4E4A] 
                            rounded-md 
                            bg-white 
                            text-[#0D4E4A] 
                            text-base
                            px-1 py-1
                            cursor-pointer 
                            shadow-sm
                            focus:outline-none 
                            focus:ring-1 
                            focus:ring-[#0D4E4A]/60
                        "
                    >
                        <option value="en">🇬🇧</option>
                        <option value="de">🇩🇪</option>
                        <option value="ru">🇷🇺</option>
                        <option value="it">🇮🇹</option>
                        <option value="fr">🇫🇷</option>
                        <option value="zh">🇨🇳</option>
                        <option value="ja">🇯🇵</option>

                    </select>
                </div>

                {/* Hamburger Menu - Bottom Right */}
                <div className="absolute bottom-5 right-5">
                    <button
                        onClick={toggleMenu}
                        aria-label="Toggle mobile menu"
                        className="bg-transparent border-none p-1"
                    >
                        <GiHamburgerMenu size={32} color="black" className="drop-shadow-md" />
                    </button>
                </div>
            </div>

            {/* Medium Screen Section (md: 768px to 1024px) */}
            <div className="hidden md:block lg:hidden">
                {/* Top Section */}
                <div
                    className={`flex items-center justify-between text-black ${isScrolled ? 'shadow-sm' : ''}`}
                    style={{
                        backgroundColor: '#FFFFFF',
                        height: '120px',
                        borderBottom: '5px solid #0D4E4A',
                        padding: '0 12px',
                        position: 'relative',
                    }}
                >
                    <div className="absolute top-3 left-3">
                    </div>
                    {/* Centered Logo */}
                    <div
                        className="flex justify-center w-full"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <img src={logo} alt="Mawathage Services" style={{ height: '140px' }} />
                    </div>

                    {/* Social Icons and Clock in Top Right Corner */}
                    <div className="flex flex-col items-end gap-1" style={{ position: 'absolute', top: '12px', right: '12px' }}>
                        <div
                            className="bg-[white] px-2 py-1 rounded font-light text-[#0D4E4A] text-sm md:text-base"
                            style={{ width: 'auto', height: 'auto' }}
                        >
                            <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                        </div>
                        <h3 className="text-xs md:text-sm text-[#1F5D42]">Moragalla / Sri Lanka</h3>
                        <div className="flex gap-1 mt-1">
                            <a
                                href="tel:+94742742794"
                                className="text-white"
                                aria-label="Call us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <LuPhone size={16} color="#1F5D42" />
                            </a>
                            <a
                                href="https://maps.app.goo.gl/4zKcLwHGahXBN8Br9"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View location"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <SlLocationPin size={16} color="#1F5D42" />
                            </a>
                            <a
                                href="mailto:mawatageservices@gmail.com"
                                className="text-white"
                                aria-label="Email us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaRegEnvelope size={16} color="#1F5D42" />
                            </a>
                            <a
                                href="https://wa.me/94762236647"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contact us on WhatsApp"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaWhatsapp size={16} color="#1F5D42" />
                            </a>
                        </div>
                        <div className="mt-2 w-full max-w-[160px] self-end">
                            <select
                                onChange={handleLanguageChange}
                                value={i18n.language}
                                className="
        w-full
        border border-[#0D4E4A] 
        rounded-[5px] 
        bg-white 
        text-[#0D4E4A] 
        text-xs md:text-sm
        px-2 py-1.5
        cursor-pointer 
        shadow-sm
        focus:outline-none 
        focus:ring-1 
        focus:ring-[#0D4E4A]/60
      "
                            >
                                <option value="en">🇬🇧 English</option>
                                <option value="de">🇩🇪 German</option>
                                <option value="ru">🇷🇺 Russian</option>
                                <option value="it">🇮🇹 Italiano</option>
                                <option value="fr">🇫🇷 Français</option>
                                <option value="zh">🇨🇳 中文</option>
                                <option value="ja">🇯🇵 日本語</option>

                            </select>
                        </div>
                    </div>
                </div>

                {/* Bottom Section (Medium Screen Navigation) */}
                <div
                    className="navbar-surface flex justify-between items-center px-2 py-2"
                    style={{
                        backgroundColor: '#1F5D42',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                        overflowX: 'auto',
                    }}
                >
                    {/* Centered Navigation Links */}
                    <div className="flex justify-center flex-grow">
                        <ul className="navbar-links flex mb-0 list-none text-sm md:text-base font-serif font-light whitespace-nowrap">
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/home"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('home')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/about-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('aboutUs')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/rooms"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('accommodations')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/ayurveda"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('medicine')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/vehicle-rental"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('vehicleRental')}
                                </NavLink>
                            </li>

                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/tours"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('tours')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 py-2 block hover:text-[#E4AB58]"
                                    to="/contact-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('contactUs')}
                                </NavLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Large Screen Section (lg and larger, ≥1024px) */}
            <div className="hidden lg:block">
                {/* Top Section */}
                <div
                    className={`flex items-center justify-between text-black ${isScrolled ? 'shadow-sm' : ''}`}
                    style={{
                        backgroundColor: '#FFFFFF',
                        height: '220px',
                        borderBottom: '5px solid #0D4E4A',
                        padding: '0 16px',
                        position: 'relative',
                    }}
                >
                    <div
                        className="absolute top-6 left-6 z-10"
                    >
                    </div>
                    {/* Centered Logo */}
                    <div
                        className="flex justify-center w-full mt-2"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <img src={logo} alt="Mawathage Services" style={{ height: '220px' }} />
                    </div>

                    {/* Social Icons and Clock in Top Right Corner */}
                    <div className="flex flex-col items-end gap-2" style={{ position: 'absolute', top: '30px', right: '16px' }}>
                        <div
                            className="bg-[white] px-2 py-1 rounded text-[#0D4E4A] text-lg md:text-xl font-light"
                            style={{ width: 'auto', height: '32px' }}
                        >
                            <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                        </div>
                        <h3 className="text-sm md:text-base text-[#1F5D42]">Moragalla / Sri Lanka</h3>
                        <div className="flex gap-1.5 mt-2">
                            <a
                                href="tel:+94742742794"
                                className="text-white"
                                aria-label="Call us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <LuPhone size={20} color="#1F5D42" />
                            </a>
                            <a
                                href="https://maps.app.goo.gl/4zKcLwHGahXBN8Br9"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View location"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <SlLocationPin size={20} color="#1F5D42" />
                            </a>
                            <a
                                href="mailto:mawatageservices@gmail.com"
                                className="text-white"
                                aria-label="Email us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaRegEnvelope size={20} color="#1F5D42" />
                            </a>
                            <a
                                href="https://wa.me/94762236647"
                                className="text-white"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contact us on WhatsApp"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#FFFFFF';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaWhatsapp size={20} color="#1F5D42" />
                            </a>
                        </div>
                        <div className="mt-3 mr-1">
                            <select
                                onChange={handleLanguageChange}
                                value={i18n.language}
                                className="
        border border-[#0D4E4A] 
        rounded-[5px] 
        bg-white 
        text-[#0D4E4A] 
        text-sm md:text-base 
        px-3 py-1.5 
        cursor-pointer 
        min-w-[140px]
        shadow-sm
        focus:outline-none 
        focus:ring-1 
        focus:ring-[#0D4E4A]/60
      "
                            >
                                <option value="en">🇬🇧 English</option>
                                <option value="de">🇩🇪 German</option>
                                <option value="ru">🇷🇺 Russian</option>
                                <option value="it">🇮🇹 Italiano</option>
                                <option value="fr">🇫🇷 Français</option>
                                <option value="zh">🇨🇳 中文</option>
                                <option value="ja">🇯🇵 日本語</option>
                                <option value="cs">🇨🇿 Čeština</option>
                                <option value="pl">🇵🇱 Polski</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Bottom Section (Desktop Navigation) */}
                <div
                    className="navbar-surface flex justify-between items-center px-4 py-2 md:px-2 md:py-2 lg:px-6 xl:px-6"
                    style={{
                        backgroundColor: '#1F5D42',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                    }}
                >
                    {/* Centered Navigation Links */}
                    <div className="flex justify-center flex-grow">
                        <ul className="navbar-links flex mb-0 list-none text-lg md:text-xl lg:text-2xl xl:text-3xl font-serif font-light">
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/home"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('home')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/about-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('aboutUs')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/rooms"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('accommodations')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/ayurveda"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('medicine')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/vehicle-rental"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('vehicleRental')}
                                </NavLink>
                            </li>

                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/tours"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('tours')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-white px-2 md:px-3 py-2 block hover:text-[#E4AB58]"
                                    to="/contact-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                >
                                    {t('contactUs')}
                                </NavLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div
                    className="block md:hidden fixed top-0 right-0 h-screen w-4/5 max-w-[320px] 
               bg-white shadow-[-6px_0_20px_rgba(0,0,0,0.25)] 
               border-l border-gray-200 z-[1001] p-6 overflow-y-auto"
                    style={{ animation: 'slideIn 0.35s ease-out' }}
                >
                    {/* Close Button */}
                    <button
                        onClick={toggleMenu}
                        aria-label="Close menu"
                        className="absolute top-5 right-5 bg-[#0D4E4A] text-white rounded-full w-10 h-10 
                 flex items-center justify-center text-xl font-bold hover:bg-[#E4AB58] 
                 transition-all shadow-md"
                    >
                        ×
                    </button>

                    {/* Resort Selector */}
                    <div className="mt-16 mb-6">
                    </div>

                    {/* Language Selector */}
                    <div className="mb-8">
                        <label className="block text-sm text-gray-700 font-medium mb-2">
                            {t('language')}
                        </label>
                        <select
                            onChange={handleLanguageChange}
                            value={i18n.language}
                            className="
          w-full border border-gray-300 rounded-lg 
                    bg-white text-[#0D4E4A] px-4 py-2.5 
          text-base cursor-pointer focus:outline-none 
                    focus:ring-2 focus:ring-[#0D4E4A]/40 shadow-sm
        "
                        >
                            <option value="en">🇬🇧 English</option>
                            <option value="de">🇩🇪 German</option>
                            <option value="ru">🇷🇺 Russian</option>
                            <option value="it">🇮🇹 Italiano</option>
                            <option value="fr">🇫🇷 Français</option>
                            <option value="zh">🇨🇳 中文</option>
                            <option value="ja">🇯🇵 日本語</option>
                            <option value="cs">🇨🇿 Čeština</option>
                            <option value="pl">🇵🇱 Polski</option>
                        </select>
                    </div>

                    {/* Navigation Links */}
                    <ul className="navbar-links flex flex-col mt-[30px]">
                        <li>
                            <NavLink
                                to="/home"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('home')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/about-us"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('aboutUs')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/rooms"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('accommodations')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/ayurveda"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('medicine')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/vehicle-rental"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('vehicleRental')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/tours"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('tours')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/contact-us"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#E4AB58] py-3 px-5 text-lg sm:text-[1.5rem] font-serif font-light"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('contactUs')}
                            </NavLink>
                        </li>
                    </ul>

                    {/* Social Icons */}
                    <div className="flex justify-center gap-3 mt-4 pt-4 border-t border-gray-200">
                        <a
                            href="tel:+94742742794"
                            aria-label="Call us"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <LuPhone size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/4zKcLwHGahXBN8Br9"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View location"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <SlLocationPin size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="mailto:mawatageservices@gmail.com"
                            aria-label="Email us"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaRegEnvelope size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                        <a
                            href="https://wa.me/94762236647"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contact us on WhatsApp"
                            className="text-[#0D4E4A]"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#E4AB58';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#0D4E4A';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaWhatsapp size={20} className="sm:size-24" color="#0D4E4A" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;